import { useEffect, useMemo } from "react";
import { Link as StyledLink } from "../../../components/Link/Link";
import { Meta } from "../../../components/Meta/Meta";
import { Box, Stack, Text } from "@chakra-ui/react";
import { CityPill } from "../../../components/CityPill/CityPill";
import { CityPillSkeleton } from "../../../components/CityPill/CityPillSkeleton";
import { useReq } from "@larner.dev/use-req";
import {
  getAgeRangeString,
  getDateTimeStartString,
  PastSparkForUser,
  Spark,
  SparkParticipantStatus,
  UpcomingSparkForUser,
} from "@greenflagdate/shared";
import { apiReq } from "../../../lib/apiReq";
import { useStoreData } from "../../../lib/store/store";
import { isLoggedIn } from "../../../lib/isLoggedIn";
import { inviterCountBySpark } from "../../../lib/inviterCounts";
import { EventCard } from "../../../components/EventCard/EventCard";
import { EventCardSkeleton } from "../../../components/EventCard/EventCardSkeleton";
import { UpcomingEligibleWidgetSkeleton } from "../../../components/UpcomingEligibleWidget/UpcomingEligibleWidgetSkeleton";
import { MatchesChip } from "../../../components/MatchesChip/MatchesChip";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { pickEventsWidget } from "./eventsWidgets";
import { useBoundaryClock } from "../../../lib/useBoundaryClock";
import { eventSoonAt } from "../../../lib/useIsEventSoon";
import { hasUnfinishedMatching } from "../../../components/PostEventMatchNudge/hasPostEventMatchNudge";

const ageInRange = (age: number, spark: Spark) => {
  if (age < spark.min_age) return false;
  if (spark.max_age && age > spark.max_age) return false;
  return true;
};


const formatEventDate = (starts_at: string) =>
  getDateTimeStartString(starts_at);

export const Events = () => {
  const navigate = useNavigate();
  const [sparksReq, sparksReqState] =
    useReq<UpcomingSparkForUser[]>(apiReq);
  const storeData = useStoreData();
  const loggedIn = useMemo(() => isLoggedIn(storeData), [storeData]);

  // Logged-in users get all upcoming sparks (registered and not) via
  // /user/state → storeData.upcomingSparks, so we only hit /spark/current
  // for logged-out visitors. Wait until store.loading resolves so we don't
  // race a token-holding user and fire /spark/current before /user/state
  // has had a chance to populate the store.
  useEffect(() => {
    if (storeData.loading) return;
    if (!loggedIn) sparksReq.get("/spark/current");
  }, [sparksReq, loggedIn, storeData.loading]);

  const allSparks: UpcomingSparkForUser[] = useMemo(
    () =>
      loggedIn ? storeData.upcomingSparks ?? [] : sparksReqState.result ?? [],
    [loggedIn, storeData.upcomingSparks, sparksReqState.result]
  );

  // These cards don't render the inviter separately (unlike EventV2), so add
  // the viewer's inviters back into each spark's attending count — otherwise an
  // invited user's chip undercounts or disappears. See inviterCountBySpark.
  const inviterCounts = useMemo(
    () => inviterCountBySpark(storeData.incomingInvitations),
    [storeData.incomingInvitations]
  );
  const attendingCount = (spark: UpcomingSparkForUser) =>
    (spark.attendingLikedCount + (inviterCounts.get(spark.id) ?? 0)) ||
    undefined;

  const registeredEvents: UpcomingSparkForUser[] = useMemo(
    () => allSparks.filter((s) => s.registered),
    [allSparks]
  );

  // Past sparks where the user has finished matching — either they completed
  // the flow or they've already swiped on every dating-eligible attendee.
  // Still shown (in a separate section below) so they can revisit who they
  // matched with; the top-of-page MatchFillWidget handles events that still
  // have undecided candidates.
  const recentEvents: PastSparkForUser[] = useMemo(() => {
    const pastSparks = storeData.pastSparks ?? [];
    // Mode-aware: for a Friends Only viewer every participant is a "friend",
    // so a hardcoded "dating" check called every past event finished and filed
    // it here — the same blind spot that hid the match widget from them.
    const friendsOnly = storeData.user?.friends_only ?? false;
    return pastSparks.filter((s) =>
      s.matchesCompleted ? true : !hasUnfinishedMatching(s, friendsOnly)
    );
  }, [storeData.pastSparks, storeData.user?.friends_only]);

  // The page stays mounted (KeepAliveTabs), so re-pick the top widget when
  // an event ends or a registered one reaches its "soon" mark, rather than
  // only when the store changes.
  const widgetBoundaries = useMemo(
    () =>
      allSparks.flatMap((s) => [
        new Date(s.ends_at).getTime(),
        ...(s.registered ? [eventSoonAt(s.starts_at)] : []),
      ]),
    [allSparks]
  );
  const now = useBoundaryClock(widgetBoundaries);

  const topWidget = useMemo(
    () =>
      pickEventsWidget({
        store: storeData,
        sparks: allSparks,
        now,
      }),
    [storeData, allSparks, now]
  );

  const unregisteredEvents = useMemo(() => {
    const unregistered = allSparks.filter(
      (s) => !s.registered && s.id !== topWidget?.featuredSparkId
    );

    unregistered.sort((a, b) => {
      const aEnds = new Date(a.ends_at).getTime();
      const bEnds = new Date(b.ends_at).getTime();
      if (!loggedIn || !storeData.user?.birth_date) return aEnds - bEnds;
      const age = dayjs().diff(storeData.user.birth_date, "years");
      const aIn = ageInRange(age, a);
      const bIn = ageInRange(age, b);
      if (aIn && !bIn) return -1;
      if (bIn && !aIn) return 1;
      return aEnds - bEnds;
    });
    return unregistered;
  }, [allSparks, loggedIn, storeData.user?.birth_date, topWidget?.featuredSparkId]);

  const goToDetails = (sparkId: number) =>
    navigate(`/event/atlanta/${sparkId}`, { state: { animate: true } });

  // True while events are still loading, for both auth states. Logged-out
  // users only fire /spark/current after storeData.loading resolves (via the
  // effect above), so we stay loading through that gap — until the request
  // returns a result or errors. Logged-in users get sparks alongside
  // /user/state, so storeData.loading is the right gate.
  const eventsLoading =
    storeData.loading ||
    (!loggedIn && !sparksReqState.result && !sparksReqState.error);

  if (!sparksReqState.loading && sparksReqState.error) {
    return <div>{sparksReqState.error.toString()}</div>;
  }

  const welcomeHeading =
    loggedIn && storeData.user?.first_name
      ? `Welcome, ${storeData.user.first_name}!`
      : "Welcome to Green Flag!";
  const welcomeSubheading = topWidget?.subheading ?? "It's time to match!";
  // When a widget is present we render a shorter greeting above the
  // widget's own subheading — "Welcome, {first_name}!" or just "Welcome!".
  const widgetGreeting = storeData.user?.first_name
    ? `Welcome, ${storeData.user.first_name}!`
    : "Welcome!";

  return (
    <>
      <Meta>
        <title>Green Flag Events</title>
        <meta property="og:title" content="Green Flag Events" />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.greenflagdate.com/events"
        />
      </Meta>
      <Box bg="bg.canvas" flex={1} display="flex" flexDirection="column">
        {eventsLoading ? (
          // Loading: mirror widget mode — green hero with the generic greeting
          // plus a widget skeleton in the gradient-backed section below, so the
          // top of the page doesn't pop from plain-hero to widget-mode once
          // data resolves.
          <>
            <Box
              bg="bg.brand"
              color="fg.onBrand"
              textAlign="center"
              pt="3"
              pb="8"
              px="6"
            >
              <Box textStyle="welcomeHeading" maxW="40rem" mx="auto">
                {widgetGreeting}
                <br />
                {welcomeSubheading}
              </Box>
            </Box>
            <Box
              px="6"
              mb="10"
              css={{
                background:
                  "linear-gradient(to bottom, var(--chakra-colors-bg-brand) 50%, transparent 50%)",
              }}
            >
              <Box maxW="420px" mx="auto" w="full">
                <Box textAlign="center" mb="3">
                  <CityPillSkeleton />
                </Box>
                <UpcomingEligibleWidgetSkeleton />
              </Box>
            </Box>
          </>
        ) : topWidget ? (
          // Widget mode: a standard green hero holds the greeting and
          // subheading. The widget sits in its own section just below,
          // backed by a gradient that blends the bottom of the hero into
          // the page background — top half of the widget's row reads as
          // green, bottom half as canvas.
          <>
            <Box
              bg="bg.brand"
              color="fg.onBrand"
              textAlign="center"
              pt="3"
              pb="8"
              px="6"
            >
              <Box textStyle="welcomeHeading" maxW="40rem" mx="auto">
                {widgetGreeting}
                <br />
                {topWidget.subheading ?? welcomeSubheading}
              </Box>
            </Box>
            <Box
              px="6"
              mb="10"
              css={{
                background:
                  "linear-gradient(to bottom, var(--chakra-colors-bg-brand) 50%, transparent 50%)",
              }}
            >
              <Box maxW="420px" mx="auto" w="full">
                {topWidget.featuredSparkId !== undefined && (
                  <Box textAlign="center" mb="3">
                    <CityPill />
                  </Box>
                )}
                <Box key={topWidget.key}>{topWidget.render()}</Box>
              </Box>
            </Box>
          </>
        ) : (
          <Box
            bg="bg.brand"
            color="fg.onBrand"
            textAlign="center"
            pt="3"
            pb="8"
            px="6"
          >
            <Box textStyle="welcomeHeading" maxW="40rem" mx="auto">
              {welcomeHeading}
            </Box>
          </Box>
        )}
        <Box
          maxW="40rem"
          mx="auto"
          mt={eventsLoading || topWidget ? "0" : "6"}
          px="6"
          w="full"
        >
          {loggedIn && !!registeredEvents.length && (
            <Stack gap="3" mb="8">
              <Text textStyle="eyebrow" color="fg.default">
                My Registrations
              </Text>
              {registeredEvents.map((spark) => (
                <EventCard
                  key={spark.id}
                  sparkId={spark.id}
                  date={formatEventDate(spark.starts_at)}
                  title={spark.title}
                  place={spark.location}
                  ages={getAgeRangeString(spark.min_age, spark.max_age, spark.age_range_label).display}
                  img={spark.primary_image || spark.hero_image || ""}
                  sparks={attendingCount(spark)}
                  sparkAvatars={spark.attendingLikedAvatars.map((a) => a.url)}
                  onClick={() => goToDetails(spark.id)}
                />
              ))}
            </Stack>
          )}
          {eventsLoading ? (
            <Stack gap="3">
              <Text textStyle="eyebrow" color="fg.default">
                Explore Upcoming Events
              </Text>
              {Array.from({ length: 3 }).map((_, i) => (
                <EventCardSkeleton key={i} />
              ))}
            </Stack>
          ) : unregisteredEvents.length ? (
            <Stack gap="3">
              <Text textStyle="eyebrow" color="fg.default">
                Explore Upcoming Events
              </Text>
              {unregisteredEvents.map((spark) => (
                <EventCard
                  key={spark.id}
                  sparkId={spark.id}
                  date={formatEventDate(spark.starts_at)}
                  title={spark.title}
                  place={spark.location}
                  ages={getAgeRangeString(spark.min_age, spark.max_age, spark.age_range_label).display}
                  img={spark.primary_image || spark.hero_image || ""}
                  sparks={attendingCount(spark)}
                  sparkAvatars={spark.attendingLikedAvatars.map((a) => a.url)}
                  onClick={() => goToDetails(spark.id)}
                />
              ))}
            </Stack>
          ) : (
            !registeredEvents.length &&
            !recentEvents.length && (
              <Box mb="8">
                We don't have any events scheduled at this time. If you are
                interested in hosting an event in your city please{" "}
                <StyledLink to="/contact">let us know!</StyledLink>
              </Box>
            )
          )}
          {loggedIn && !!recentEvents.length && (
            <Stack gap="3" mt="8">
              <Text textStyle="eyebrow" color="fg.default">
                My Recent Events
              </Text>
              {recentEvents.map((spark) => {
                const datingMatches = spark.participants.filter(
                  (p) =>
                    p.category === "dating" &&
                    p.status === SparkParticipantStatus.MutualSpark
                ).length;
                const friendMatches = spark.participants.filter(
                  (p) =>
                    p.category === "friend" &&
                    p.status === SparkParticipantStatus.MutualSpark
                ).length;
                const chips =
                  datingMatches > 0 || friendMatches > 0 ? (
                    <>
                      {datingMatches > 0 && (
                        <MatchesChip count={datingMatches} tone="dating" />
                      )}
                      {friendMatches > 0 && (
                        <MatchesChip count={friendMatches} tone="friends" />
                      )}
                    </>
                  ) : undefined;
                return (
                  <EventCard
                    key={spark.id}
                    sparkId={spark.id}
                    date={formatEventDate(spark.starts_at)}
                    title={spark.title}
                    place={spark.location}
                    ages={getAgeRangeString(spark.min_age, spark.max_age, spark.age_range_label).display}
                    img={spark.primary_image || spark.hero_image || ""}
                    extraChips={chips}
                    onClick={() =>
                      navigate(`/event/${spark.id}/match`, {
                        state: { animate: true },
                      })
                    }
                  />
                );
              })}
            </Stack>
          )}
        </Box>
      </Box>
    </>
  );
};
